import { NavLink } from 'react-router-dom';
import { Icon, type IconName } from './Icon';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '@/app/AuthContext';
import { useSelection } from '@/app/SelectionContext';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { Permission } from '@/types/permission';
import type { OperationalModule } from '@/types/operational';
import { v } from '@/theme/tokens';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  visible?: (ctx: { hasProduction: boolean; canViewAudit: boolean }) => boolean;
}

/**
 * «Операционный контроль» — a section, not a page.
 *
 * Each entry appears only when the person actually holds the module behind
 * it. That is presentation following authorization: the server refuses these
 * routes independently, and an item hidden here is not what protects them.
 * The owner holds every module, so the owner sees the whole section.
 */
interface OperationalNavItem extends NavItem {
  module: OperationalModule;
}

export const OPERATIONAL_NAV: OperationalNavItem[] = [
  { to: '/operational', label: 'Обзор', icon: 'grid', end: true, module: 'INVENTORY' },
  { to: '/operational/inventory', label: 'Инвентаризация', icon: 'clipboard', module: 'INVENTORY' },
  { to: '/operational/suppliers', label: 'Поставщики и долги', icon: 'truck', module: 'SUPPLIERS_AP' },
  { to: '/operational/documents', label: 'Документы', icon: 'scan', module: 'DOCUMENTS_OCR' },
];

/** The left menu. Exported so navigation tests assert the real list
 * rather than a copy of it that can drift. */
export const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Обзор', icon: 'grid', end: true },
  { to: '/company', label: 'Вся компания', icon: 'building' },
  { to: '/pnl', label: 'Отчёт ОПУ', icon: 'table' },
  { to: '/expenses', label: 'Расходы', icon: 'coins' },
  {
    to: '/production',
    label: 'Производство',
    icon: 'factory',
    visible: ({ hasProduction }) => hasProduction,
  },
  { to: '/units', label: 'Объекты', icon: 'building' },
  { to: '/compare', label: 'Сравнение', icon: 'compare' },
  { to: '/alerts', label: 'Требует внимания', icon: 'alert' },
  { to: '/imports', label: 'Импорты', icon: 'inbox' },
  {
    to: '/audit',
    label: 'Журнал аудита',
    icon: 'doc',
    visible: ({ canViewAudit }) => canViewAudit,
  },
];

export function Sidebar() {
  const { user, can } = useAuth();
  const { units } = useSelection();
  const operational = useOperationalAccess();
  const hasProduction = units.some((u) => u.type === 'PRODUCTION_KITCHEN');
  const canViewAudit = can(Permission.VIEW_AUDIT);

  // «Обзор» is shown when the person has any module at all; every other item
  // needs its own. A person with only SUPPLIERS_AP gets the overview and the
  // suppliers page, and no inventory link.
  const operationalItems = OPERATIONAL_NAV.filter((item) =>
    item.end ? operational.hasAny : operational.can(item.module),
  );

  // Geometry stays here; colour, weight and the active capsule live in
  // styles/glass.css, so the glass and classic skins can each draw them.
  const linkStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    padding: '9px 12px',
    marginBottom: 2,
    fontSize: 13.5,
  } as const;
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'rt-nav-item rt-nav-item--active' : 'rt-nav-item';

  return (
    <aside
      className="rt-sidebar"
      style={{
        width: 236,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <div
          className="rt-brand-mark"
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: v('accent'),
            color: v('on-accent'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: -0.3,
          }}
        >
          RT
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14.5, letterSpacing: -0.2 }}>Ramen Ten</div>
          <div className="rt-vibrant-secondary" style={{ fontSize: 11.5 }}>Финансовый контроль</div>
        </div>
      </div>

      <nav className="rt-sidebar__nav" aria-label="Разделы" style={{ padding: '6px 12px', flex: 1 }}>
        {NAV_ITEMS.filter((item) => !item.visible || item.visible({ hasProduction, canViewAudit })).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={linkClass}
            style={linkStyle}
          >
            <span className="rt-nav-item__icon" style={{ display: 'flex' }}>
              <Icon name={item.icon} size={17} />
            </span>
            <span style={{ flex: 1 }}>{item.label}</span>
          </NavLink>
        ))}

        {operationalItems.length > 0 && (
          <>
            <div
              className="rt-vibrant-tertiary"
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                padding: '16px 12px 6px',
              }}
            >
              Операционный контроль
            </div>
            {operationalItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={linkClass}
                style={linkStyle}
              >
                <span className="rt-nav-item__icon" style={{ display: 'flex' }}>
                  <Icon name={item.icon} size={17} />
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.label}
                </span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div className="rt-sidebar__footer" style={{ padding: '10px 12px' }}>
        <ThemeToggle />
        {user?.permissions.includes(Permission.MANAGE_SETTINGS) && (
          <NavLink to="/settings" className={linkClass} style={{ ...linkStyle, marginTop: 8 }}>
            <span className="rt-nav-item__icon" style={{ display: 'flex' }}>
              <Icon name="gear" size={17} />
            </span>
            Настройки
          </NavLink>
        )}
      </div>
    </aside>
  );
}
